import { DndContext, DragOverlay } from '@dnd-kit/core';
import { t } from 'i18next';
import React, { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import withTooltip from 'Components/hoc/withTooltip/withTooltip';
import SSpecialtyButton from 'Components/structural/SSpecialtyButton/SSpecialtyButton';
import { useAppDispatch } from 'Libs/redux/store';
import { useTypedSelector } from 'Libs/redux/utils';
import { redirect } from 'Libs/routing';
import { enqueueBasicAlert } from 'Pages/AlertManager';
import { ContentDrawer } from 'Pages/Activity/Campaign/components/ContentDrawer/ContentDrawer';
import { useContentsDragAndDrop } from 'Pages/Activity/Campaign/components/DraggableContentCard/hooks/useContentsDragAndDrop';
import { inferSessionStartDate } from 'Pages/Activity/Campaign/dates';
import { withCampaignHoc } from 'Pages/Activity/Campaign/hoc/withCampaign';
import { CampaignFormValues } from 'Pages/Activity/Campaign/saveCampaign.hook';
import { saveCampaign } from 'Store/entities/activity/activity.thunks';
import type { PartialContentForCampaignForm } from 'Store/entities/activity/activity.type';
import { DrawerContent } from 'Store/entities/content/content-drawer/content-drawer.types';

import { CampaignSession } from './CampaignSession';

const SSpecialtyButtonWithTooltip = withTooltip(SSpecialtyButton);

const ALERT_DURATION = 5000;

export const CampaignSessionList = withCampaignHoc(({ campaign }) => {
  const dispatch = useAppDispatch();
  const { locked, selectedLanguage } = useTypedSelector((state) => state.entities.activity.edition);
  const [displayContentDrawer, setDisplayContentDrawer] = useState(false);
  const [selectedSessionIndex, setSelectedSessionIndex] = useState<number | undefined>();

  const { watch, setValue } = useFormContext<CampaignFormValues>();

  const isDefaultLanguage = useMemo(() => selectedLanguage === campaign.languages[0], [selectedLanguage, campaign]);

  const sessionsWatch = useMemo(() => {
    if (watch('mode') === 'random') {
      return [[...(watch('contents') || [])]];
    }

    const orderedSessions = watch('orderedSessions');
    if (orderedSessions && orderedSessions.length > 0) {
      return orderedSessions;
    }
    return [[]];
  }, [watch('contents'), watch('orderedSessions')]);

  const allContentIdsSet = useMemo(
    () => new Set(sessionsWatch.flat().map(({ contentId }) => contentId)),
    [sessionsWatch],
  );

  const handleDisplayContentDrawerFactory = useCallback(
    (sessionIndex: number) => () => {
      setSelectedSessionIndex(sessionIndex);
      setDisplayContentDrawer(true);
    },
    [],
  );

  const handleDrawerClose = useCallback(() => {
    setDisplayContentDrawer(false);
    setSelectedSessionIndex(undefined);
  }, []);

  const handleDrawerItemSelect = useCallback(
    (contents: DrawerContent[]) => {
      if (selectedSessionIndex === undefined) {
        return;
      }

      const newSession = [
        ...sessionsWatch[selectedSessionIndex],
        ...contents.map<PartialContentForCampaignForm>((item) => ({
          id: item.uiId,
          identifier: item.customId,
          gameType: item.gameType,
          contentId: item.id,
          statuses: item.statuses,
          name: item.title,
          coverImage: { url: item.coverUrl },
          archived: item.archived,
        })),
      ];

      if (watch('mode') === 'random') {
        setValue('contents', newSession);
      } else {
        setValue(
          'orderedSessions',
          sessionsWatch.map((session, index) => (index === selectedSessionIndex ? newSession : session)),
          { shouldDirty: true },
        );
      }

      return handleDrawerClose();
    },
    [selectedSessionIndex, sessionsWatch, watch('mode')],
  );

  const handleOnNewContent = useCallback(async () => {
    const { unwrap } = dispatch(saveCampaign(campaign));

    try {
      await unwrap();

      redirect('/content?new=true');
    } catch (error: unknown) {
      enqueueBasicAlert(
        {
          id: 'campaign_not_saved',
          status: 'error',
          title: t('activities:alerts.errors.save_error_message', {
            message: t('activities:alerts.errors.save_error_title'),
          }),
        },
        ALERT_DURATION,
      );
    }
  }, [handleDisplayContentDrawerFactory]);

  const handleAddSession = useCallback(() => {
    setValue('orderedSessions', [...sessionsWatch, []]);
  }, [sessionsWatch]);

  const handleDeleteSession = useCallback(
    (sessionIndex: number) => () => {
      setValue(
        'orderedSessions',
        watch('orderedSessions')?.filter((_, index) => index !== sessionIndex),
        { shouldDirty: true },
      );
    },
    [watch('orderedSessions')],
  );

  const handleDeleteContent = useCallback(
    (sessionIndex: number) => (contentId: string) => {
      const newSession = sessionsWatch[sessionIndex].filter(({ id }) => id !== contentId);

      if (watch('mode') === 'manual') {
        setValue(
          'orderedSessions',
          sessionsWatch.map((session, index) => (index === sessionIndex ? newSession : session)),
          { shouldDirty: true },
        );

        return;
      }

      setValue(
        'contents',
        watch('contents')?.filter(({ id }) => id !== contentId),
        { shouldDirty: true },
      );
    },
    [sessionsWatch, watch('mode')],
  );

  const handleSessionChange = useCallback((newSessions: PartialContentForCampaignForm[][]) => {
    if (watch('mode') === 'random') {
      setValue('contents', newSessions.flat());
    } else {
      setValue('orderedSessions', newSessions);
    }
  }, []);

  const { overlayItem, contextProps } = useContentsDragAndDrop({
    onSessionChange: handleSessionChange,
    sessions: sessionsWatch,
    selectedLanguage,
  });

  return (
    <DndContext {...contextProps}>
      <ContentDrawer
        visible={displayContentDrawer}
        onClose={handleDrawerClose}
        onNewContent={handleOnNewContent}
        onItemsSelect={handleDrawerItemSelect}
        selectedLanguage={selectedLanguage}
        alreadyUsedItemsId={allContentIdsSet}
      />
      <DragOverlay zIndex={100000}>{overlayItem}</DragOverlay>
      {sessionsWatch.map((session, index) => {
        const sessionNumber = index + 1;

        return (
          <CampaignSession
            key={index}
            id={index}
            selectedLanguage={selectedLanguage}
            locked={locked}
            session={session}
            sessionNumber={index + 1}
            startDate={inferSessionStartDate(watch, sessionNumber)}
            random={watch('mode') === 'random'}
            onAddContent={handleDisplayContentDrawerFactory(index)}
            onDeleteContent={handleDeleteContent(index)}
            onDeleteSession={handleDeleteSession(index)}
          />
        );
      })}
      {watch('mode') === 'manual' && (
        <SSpecialtyButtonWithTooltip
          text={t('activities:scheduling.add_daily_session')}
          onClick={() => handleAddSession()}
          disabled={!isDefaultLanguage || locked}
          tooltipPosition="top"
          tooltipContent={t('activities:scheduling.add_daily_session_tooltip')}
          tooltipEnabled={!locked}
        />
      )}
    </DndContext>
  );
});
